import { useCallback, useEffect, useRef, useState } from 'react'
import { chargerMoi, chargerPreferences, chargerReglages, type Resultat } from './soirees-maison-api.js'
import { PREFERENCES_DEFAUT, tvaDefautDe, type Preferences } from './reglages-billetterie-model.js'
import { garderApresErreur, type ErreurBM } from './soirees-maison-model.js'

/**
 * SOIRÉES DE BILLETTERIE LIVE CLUB — les lectures partagées des écrans.
 *
 * Un seul chargement par document pour ce qui ne bouge presque jamais (qui je
 * suis, les réglages de Gestion) ; une lecture vivante, relue à la cadence des
 * réglages, pour le reste. Une relecture qui échoue pour une panne passagère
 * garde les dernières données affichées : l'écran dit l'erreur, il ne se vide
 * pas. Une coupure, un blocage ou un refus, si (`garderApresErreur`).
 */

let moiCache: string | null = null
let moiEnVol: Promise<string> | null = null

/** L'identifiant de la personne connectée (« inconnu » si `/me` ne répond pas). */
export function useMoi(): string | null {
  const [moi, setMoi] = useState<string | null>(moiCache)
  useEffect(() => {
    if (moiCache) return
    let vivant = true
    moiEnVol ??= chargerMoi().then((m) => (moiCache = m?.id ?? 'inconnu')).finally(() => { moiEnVol = null })
    void moiEnVol.then((m) => { if (vivant) setMoi(m) })
    return () => { vivant = false }
  }, [])
  return moi
}

let prefsCache: Preferences | null = null
const prefsAbonnes = new Set<(p: Preferences) => void>()

/** À appeler après un enregistrement des réglages : tous les écrans ouverts suivent. */
export function publierPreferences(p: Preferences): void {
  prefsCache = p
  prefsAbonnes.forEach((f) => f(p))
}

let prefsEnVol: Promise<Resultat<unknown>> | null = null

/**
 * Les réglages de Gestion (§7.1). Tant qu'ils ne sont pas lus — ou s'ils ne
 * peuvent pas l'être — les défauts du contrat s'appliquent à l'AFFICHAGE :
 * l'encart s'affiche, les soirées d'entraînement restent masquées, la cadence
 * est de 30 s. Une lecture en échec est rendue (`erreur`, `relire`) : l'assistant
 * ne pré-remplit pas en silence avec d'autres prix que ceux réglés.
 */
export function usePreferencesBilletterie(actif = true): { prefs: Preferences; lues: boolean; erreur: ErreurBM | null; relire: () => void } {
  const [prefs, setPrefs] = useState<Preferences | null>(prefsCache)
  const [erreur, setErreur] = useState<ErreurBM | null>(null)
  const [tour, setTour] = useState(0)
  const relire = useCallback(() => { setErreur(null); setTour((n) => n + 1) }, [])
  useEffect(() => {
    if (!actif) return
    let vivant = true
    prefsAbonnes.add(setPrefs)
    if (!prefsCache) {
      // Un seul appel à la fois pour tous les écrans ouverts ; chacun lit son issue.
      prefsEnVol ??= chargerPreferences().then((r) => { if (r.ok) publierPreferences(r.data.preferences); return r }).finally(() => { prefsEnVol = null })
      void prefsEnVol.then((r) => { if (vivant && !r.ok) setErreur(r.erreur) })
    }
    return () => { vivant = false; prefsAbonnes.delete(setPrefs) }
  }, [actif, tour])
  return { prefs: prefs ?? PREFERENCES_DEFAUT, lues: prefs !== null, erreur: prefs === null ? erreur : null, relire }
}

/**
 * Le taux de TVA que prendra un tarif créé sans taux : le réglage
 * `tarif.tva_bp_defaut` de la billetterie, lu une fois par écran. `null` tant
 * qu'il n'est pas lu, ou s'il ne peut pas l'être : l'écran dit alors « le
 * réglage par défaut », sans jamais inventer de chiffre.
 */
export function useTvaDefautBilletterie(): number | null {
  const [bp, setBp] = useState<number | null>(null)
  useEffect(() => {
    let vivant = true
    void chargerReglages().then((r) => { if (vivant && r.ok) setBp(tvaDefautDe(r.data.reglages)) })
    return () => { vivant = false }
  }, [])
  return bp
}

export type Lecture<T> =
  | { etat: 'chargement' }
  | { etat: 'ok'; data: T; erreur: ErreurBM | null }
  | { etat: 'erreur'; erreur: ErreurBM }

/**
 * Une lecture relue toutes les `secondes` (0 = jamais). `cle` change → nouvelle
 * lecture immédiate (filtres, identifiant). Une relecture en erreur passagère
 * GARDE les données déjà affichées, avec l'erreur à côté ; une coupure par la
 * billetterie, un blocage ou un refus passent en erreur, et la relecture
 * continue : l'écran revient seul quand l'accès est rétabli.
 */
export function useLecture<T>(charger: (() => Promise<Resultat<T>>) | null, cle: string, secondes: number): {
  lecture: Lecture<T>
  recharger: () => void
  /** Une écriture a rendu l'objet à jour : il s'affiche sans attendre la relecture. */
  poser: (data: T) => void
} {
  const [lecture, setLecture] = useState<Lecture<T>>({ etat: 'chargement' })
  const [tour, setTour] = useState(0)
  const chargerRef = useRef(charger)
  chargerRef.current = charger
  const cleLue = useRef(cle)
  const recharger = useCallback(() => setTour((n) => n + 1), [])
  const poser = useCallback((data: T) => setLecture({ etat: 'ok', data, erreur: null }), [])
  const actif = charger !== null

  useEffect(() => {
    const f = chargerRef.current
    if (!f) return
    let vivant = true
    // Un autre objet (autre soirée, autres filtres) : on n'affiche pas l'ancien en attendant.
    const autre = cleLue.current !== cle
    cleLue.current = cle
    setLecture((avant) => (avant.etat === 'ok' && !autre ? avant : { etat: 'chargement' }))
    const lireUneFois = () => {
      void f().then((r) => {
        if (!vivant) return
        setLecture((avant) => (r.ok ? { etat: 'ok', data: r.data, erreur: null }
          : avant.etat === 'ok' && garderApresErreur(r.erreur) ? { ...avant, erreur: r.erreur } : { etat: 'erreur', erreur: r.erreur }))
      })
    }
    lireUneFois()
    const t = secondes > 0 ? setInterval(lireUneFois, secondes * 1000) : null
    return () => { vivant = false; if (t) clearInterval(t) }
  }, [cle, secondes, tour, actif])

  return { lecture, recharger, poser }
}
